/**
 * Which parts commit once, and which commit in the second pass.
 *
 * Two predicates over `PartSpec` and a binding value, with no state and no DOM
 * between them. They decide the shape of a mount before any node is touched:
 * whether a part needs a reactive node at all, and where in the two-pass commit
 * order it belongs (G10).
 */
import { isAttributePartType, isElementPartType } from './render-values.js';
/** Attribute-family and element parts commit after every node-family part (G10). */
export function isDeferredPart(part) {
    return isAttributePartType(part.spec.type) || isElementPartType(part.spec.type);
}
/**
 * Part types whose commit is decided entirely by the binding value.
 *
 * The four excluded types are not. A `conditional` part re-derives what it
 * commits from `spec.truthy` / `spec.falsy`, which are typed `unknown` and may
 * themselves be signals, so even a plain `false` binding can commit reactive
 * content. `repeat`, `keyed-sequence` and `raw-html` are the part types whose
 * whole job is to read a descriptor, and a descriptor keeps its reactive
 * inputs inside itself. None of the four can be judged from the binding value,
 * so all four always keep their effect.
 */
const STATIC_ELIGIBLE_PART_TYPES = new Set([
    'node',
    'text',
    'element',
    'attribute',
    'property',
    'boolean-attribute',
    'event',
]);
/**
 * The `typeof` results a part may be bound to and still commit once.
 *
 * A Set rather than a `switch`, and an ALLOW-LIST rather than a negation: the
 * types that can never change are named, so a type nobody thought of — a
 * `symbol`, a `function`, whatever a future engine adds — is excluded by
 * default rather than admitted by default. Being wrong in the permissive
 * direction is a missed update, which is a corruption bug; being wrong in the
 * strict direction costs one effect nobody sees.
 */
const STATIC_BINDING_TYPES = new Set([
    'string',
    'number',
    'boolean',
    'bigint',
    'undefined',
]);
/**
 * Part types a static binding can commit through WITHOUT a live-part object
 * (#1398): the four commit shapes whose whole DOM effect is computable from
 * the spec and one primitive value. `event` and `element` are excluded — an
 * event part's commit routes through the shared listener memo and an element
 * part's through the valueless-marker bag, and neither is worth a second
 * commit path for a binding that is almost never static.
 */
const STATIC_SLOT_TYPES = new Set([
    'node',
    'text',
    'attribute',
    'property',
    'boolean-attribute',
]);
/** Whether a static binding of `spec` may commit through a slot, not a part. */
export function isStaticSlotType(spec) {
    return STATIC_SLOT_TYPES.has(spec.type);
}
/**
 * Whether a part's binding provably cannot change, so the part can commit once
 * and hold no reactive node.
 *
 * It is an allow-list, not a deny-list: only an eligible part type (above)
 * bound to a primitive — string, number, boolean, bigint, `null` or
 * `undefined` — is static. Nothing else is, so every reactive input the
 * renderer understands is excluded without having to be enumerated. A signal
 * or computed is a function; `repeat()`, `conditional()`, `spread()`, `ref()`,
 * `until()`, `live()`, `cache()`, raw HTML, DOM nodes, nested template
 * instances, arrays and thenables are all objects, and a descriptor that hides
 * signals inside itself is still an object; an event listener is a function or
 * an object. Symbols are excluded too — nothing in the renderer commits one
 * meaningfully, so there is no reason to widen for them.
 *
 * A composite attribute (`attr="a ${x} b ${y}"`) is rejected explicitly: its
 * binding is an array rebuilt per read whose elements may be signals, and the
 * array is what the part commits.
 *
 * Being wrong in the permissive direction is a missed update — a corruption
 * bug. Being wrong in the strict direction costs one effect nobody sees.
 */
export function isStaticBinding(spec, value) {
    if (!STATIC_ELIGIBLE_PART_TYPES.has(spec.type))
        return false;
    if (spec.type === 'attribute' && spec.bindingIndices)
        return false;
    const kind = typeof value;
    if (STATIC_BINDING_TYPES.has(kind))
        return true;
    // `typeof null === 'object'`; every other object may carry reactivity.
    return kind === 'object' && value === null;
}
//# sourceMappingURL=render-dom-static.js.map